import React, { useState } from "react";
import { syllabusGrades } from "../mock/mockData";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const gradeOrder = ["grade1", "grade2"]; // extend easily later

const gradeLabels = {
  grade1: "Grade 1",
  grade2: "Grade 2",
};

export default function SyllabusStructure() {
  const [selectedGrade, setSelectedGrade] = useState("grade1");
  const gradeData = syllabusGrades.instrumental[selectedGrade];

  return (
    <div className="space-y-8">
      {/* Page heading */}
      <section className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight text-[#182033]">
          Syllabus &amp; Examination Structure
        </h1>
        <p className="mx-auto max-w-3xl text-sm leading-relaxed text-[#4b4a46] sm:text-base">
          Structured learning aligned with a level-based conservatory framework. The
          outline below illustrates the format and expectations for selected
          instrumental grades.
        </p>
      </section>

      <section className="grid gap-6 rounded-3xl border border-[#ddcaa4] bg-[#f9f2e6] p-4 shadow-sm lg:grid-cols-[minmax(0,1.1fr)_minmax(0,2fr)] lg:p-6">
        {/* Left navigation column */}
        <aside className="space-y-4 border-r border-[#ddcaa4]/70 pr-4 lg:pr-6">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8c7648]">
              Instrumental examinations
            </p>
            <nav className="mt-2 flex flex-col gap-1">
              <button
                type="button"
                className="flex items-center justify-between rounded-md px-3 py-2 text-left text-xs font-medium tracking-wide text-[#4b4a46] hover:bg-[#f1e3cf]"
              >
                <span>Preliminary</span>
              </button>
              {gradeOrder.map((gradeKey) => (
                <button
                  key={gradeKey}
                  type="button"
                  onClick={() => setSelectedGrade(gradeKey)}
                  className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-xs font-medium tracking-wide transition ${
                    selectedGrade === gradeKey
                      ? "bg-[#e7d7bd] text-[#1f2738]"
                      : "text-[#4b4a46] hover:bg-[#f1e3cf]"
                  }`}
                >
                  <span>{gradeLabels[gradeKey]}</span>
                </button>
              ))}
              <button
                type="button"
                className="flex items-center justify-between rounded-md px-3 py-2 text-left text-xs font-medium tracking-wide text-[#a1998a]"
                disabled
              >
                <span>Grade 3–6 (illustrative)</span>
              </button>
            </nav>
          </div>

          <div className="space-y-1 pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8c7648]">
              Vocal examinations
            </p>
            <p className="text-[11px] leading-relaxed text-[#817664]">
              Vocal syllabi follow an equivalent structure, with components adapted to
              the specific requirements of vocal study.
            </p>
          </div>

          <div className="space-y-1 pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8c7648]">
              Theory &amp; musicianship
            </p>
            <p className="text-[11px] leading-relaxed text-[#817664]">
              Written and practical musicianship components are specified separately,
              with clear weightings and expectations at each level.
            </p>
          </div>
        </aside>

        {/* Right detail column */}
        <section className="space-y-4">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[#ddcaa4] pb-3">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-[#182033]">
                {gradeData.title}
              </h2>
              <p className="text-xs text-[#7b7466]">{gradeData.duration}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-[#d0bf99] bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1f2738] hover:bg-[#f5ebd9]"
            >
              Download grade syllabus (preview)
            </Button>
          </header>

          {/* Examination format + marks layout */}
          <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
            <CardHeader className="border-b border-[#e1d2b3]/60 pb-3">
              <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
                Examination format
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 px-4 py-4 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
              <ul className="space-y-1.5 text-sm leading-relaxed text-[#4b4a46]">
                {gradeData.format.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-[6px] h-1 w-1 rounded-full bg-[#8c7648]" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="rounded-xl border border-[#e1d2b3] bg-white px-3 py-3 text-xs text-[#4b4a46]">
                <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.6fr)] border-b border-[#e1d2b3]/70 pb-1 font-semibold text-[#182033]">
                  <span>Component</span>
                  <span className="text-right">Marks</span>
                </div>
                <div className="divide-y divide-[#f0e1c7]/80">
                  {gradeData.marksTable.map((row) => (
                    <div
                      key={row.component}
                      className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.6fr)] py-1.5"
                    >
                      <span>{row.component}</span>
                      <span className="text-right font-medium">{row.marks}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed components */}
          <div className="grid gap-4 md:grid-cols-2">
            <DetailCard title="Technical requirements" items={gradeData.technicalRequirements} />
            <DetailCard title="Prescribed pieces" items={gradeData.prescribedPieces} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <DetailCard title="Sight reading" items={gradeData.sightReading} />
            <DetailCard title="Aural tests" items={gradeData.auralTests} />
          </div>
        </section>
      </section>
    </div>
  );
}

function DetailCard({ title, items }) {
  return (
    <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 px-4 pb-4 text-sm leading-relaxed text-[#4b4a46]">
        {items.map((item) => (
          <p key={item}>{item}</p>
        ))}
      </CardContent>
    </Card>
  );
}
